import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { SECTORS } from "../data/sectors";
import { Reveal, SectionNumber } from "../components/Reveal";
import { ProcessFlow } from "../components/ProcessFlow";

const WHATSAPP_URL =
    "https://wa.me/971563141428?text=" +
    encodeURIComponent(
        "Hello NOMAS, I would like to discuss a sustainability/project opportunity.",
    );

const Gallery = ({ images }) => {
    const [i, setI] = useState(0);
    const prev = () => setI((i - 1 + images.length) % images.length);
    const next = () => setI((i + 1) % images.length);
    return (
        <div
            data-testid="product-gallery"
            className="relative overflow-hidden rounded-3xl border border-[#DCE5D4] bg-white"
        >
            <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${i * 100}%)` }}
            >
                {images.map((im, k) => (
                    <div key={k} className="w-full shrink-0">
                        <img
                            src={im.src}
                            alt={im.alt}
                            loading={k === 0 ? "eager" : "lazy"}
                            className="h-[320px] w-full object-contain p-6 md:h-[460px] md:p-10"
                        />
                    </div>
                ))}
            </div>
            <button
                onClick={prev}
                data-testid="gallery-prev"
                aria-label="Previous image"
                className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#DCE5D4] bg-white/90 text-[#0E1112] shadow-sm transition-colors duration-300 hover:bg-[#598B3B] hover:text-white"
            >
                <ChevronLeft size={18} />
            </button>
            <button
                onClick={next}
                data-testid="gallery-next"
                aria-label="Next image"
                className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#DCE5D4] bg-white/90 text-[#0E1112] shadow-sm transition-colors duration-300 hover:bg-[#598B3B] hover:text-white"
            >
                <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
                {images.map((_, k) => (
                    <button
                        key={k}
                        onClick={() => setI(k)}
                        data-testid={`gallery-dot-${k}`}
                        aria-label={`Image ${k + 1}`}
                        className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                            k === i ? "w-6 bg-[#598B3B]" : "w-2 bg-[#DCE5D4]"
                        }`}
                    />
                ))}
            </div>
        </div>
    );
};

export default function SectorPage() {
    const { slug } = useParams();
    const s = SECTORS[slug];
    if (!s) return <Navigate to="/" replace />;

    return (
        <main data-testid={`sector-page-${slug}`} className="pt-28 pb-24 md:pt-36">
            <div className="mx-auto max-w-[1200px] px-6 md:px-10">
                <Reveal>
                    <Link
                        to="/"
                        state={{ scrollTo: "#domains" }}
                        data-testid="back-to-solutions"
                        className="group inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.2em] text-[#5C6B57] transition-colors duration-300 hover:text-[#598B3B]"
                    >
                        <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
                        All Solutions
                    </Link>
                </Reveal>

                <Reveal delay={0.05}>
                    <p className="mt-10 text-sm uppercase tracking-[0.3em] text-[#598B3B]">
                        {s.overline}
                    </p>
                    <h1 className="font-display mt-4 max-w-3xl text-5xl font-light leading-[1.05] tracking-tighter text-[#0E1112] sm:text-6xl lg:text-7xl">
                        {s.title}
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#45543F] md:text-lg">
                        {s.lead}
                    </p>
                </Reveal>

                {s.heroImg && (
                    <Reveal delay={0.1}>
                        <figure
                            data-testid="sector-hero-image"
                            className="group relative mt-12 h-[300px] overflow-hidden rounded-3xl md:h-[440px]"
                        >
                            <img
                                src={s.heroImg.src}
                                alt={s.heroImg.alt}
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            {!s.heroImg.plain && (
                                <>
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0E1112]/55 via-transparent to-transparent" />
                                    <figcaption className="glass absolute bottom-5 left-5 rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#0E1112]">
                                        {s.heroImg.caption}
                                    </figcaption>
                                </>
                            )}
                        </figure>
                    </Reveal>
                )}

                {s.gallery && (
                    <Reveal delay={0.1}>
                        <div className="mt-12">
                            <Gallery images={s.gallery} />
                        </div>
                    </Reveal>
                )}

                <div className="mt-16 grid gap-6 md:grid-cols-2">
                    <Reveal>
                        <div className="h-full rounded-2xl border border-[#C8102E]/20 bg-[#C8102E]/[0.03] p-8">
                            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#C8102E]">
                                The Challenge
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-[#45543F] md:text-lg">
                                {s.challenge}
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="h-full rounded-2xl border border-[#598B3B]/25 bg-[#598B3B]/[0.04] p-8">
                            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#598B3B]">
                                Our Approach
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-[#45543F] md:text-lg">
                                {s.approach}
                            </p>
                        </div>
                    </Reveal>
                </div>

                {s.extraImg && (
                    <Reveal delay={0.1}>
                        <img
                            src={s.extraImg.src}
                            alt={s.extraImg.alt}
                            loading="lazy"
                            data-testid="sector-extra-image"
                            className="mt-10 w-full rounded-3xl border border-[#DCE5D4]"
                        />
                    </Reveal>
                )}

                {s.pillars && (
                    <div className="mt-16">
                        <SectionNumber n="A" label="Three Programmes" />
                        <div className="grid gap-5 md:grid-cols-3">
                            {s.pillars.map((p, i) => (
                                <Reveal key={p.title} delay={i * 0.08} className="h-full">
                                    <div
                                        data-testid={`sector-pillar-${i + 1}`}
                                        className="group flex h-full flex-col rounded-2xl border border-[#DCE5D4] bg-white p-8 transition-[border-color,transform] duration-400 hover:-translate-y-1.5 hover:border-[#598B3B]/50"
                                    >
                                        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#598B3B]/10 text-[#598B3B] transition-[background-color,color] duration-400 group-hover:bg-[#598B3B] group-hover:text-white">
                                            <p.icon size={22} strokeWidth={1.6} />
                                        </span>
                                        <h3 className="font-display mt-6 text-2xl font-medium leading-snug tracking-tight text-[#0E1112]">
                                            {p.title}
                                        </h3>
                                        <p className="mt-3 text-sm leading-relaxed text-[#5C6B57]">
                                            {p.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-16">
                    <SectionNumber n={s.pillars ? "B" : "A"} label="Process Flow" />
                    <ProcessFlow steps={s.flow} tags={s.flowTags} testid="sector-flow" />
                </div>

                {s.services && (
                    <div className="mt-16">
                        <SectionNumber n="B" label="Services Included" />
                        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {s.services.map((sv, i) => (
                                <Reveal key={sv.name} delay={i * 0.06} className="h-full">
                                    <article
                                        data-testid={`sector-service-${i + 1}`}
                                        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#DCE5D4] bg-white transition-[border-color,transform] duration-400 hover:-translate-y-1.5 hover:border-[#598B3B]/50"
                                    >
                                        <div className="h-44 overflow-hidden">
                                            <img
                                                src={sv.img}
                                                alt={sv.name}
                                                loading="lazy"
                                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                        </div>
                                        <div className="p-6">
                                            <h3 className="font-display text-xl font-medium tracking-tight text-[#0E1112]">
                                                {sv.name}
                                            </h3>
                                            <p className="mt-2 text-sm leading-relaxed text-[#5C6B57]">
                                                {sv.desc}
                                            </p>
                                        </div>
                                    </article>
                                </Reveal>
                            ))}
                        </div>
                        {s.alsoIncluded && (
                            <Reveal delay={0.1}>
                                <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="also-included">
                                    <span className="mr-2 text-xs font-bold uppercase tracking-[0.2em] text-[#8CA093]">
                                        Also included
                                    </span>
                                    {s.alsoIncluded.map((a) => (
                                        <span
                                            key={a}
                                            className="rounded-full border border-[#DCE5D4] bg-[#F7F9F5] px-4 py-1.5 text-xs font-medium uppercase tracking-[0.14em] text-[#5C6B57]"
                                        >
                                            {a}
                                        </span>
                                    ))}
                                </div>
                            </Reveal>
                        )}
                    </div>
                )}

                {s.features && (
                    <div className="mt-16">
                        <SectionNumber n="C" label="Capabilities" />
                        <div className="grid gap-5 md:grid-cols-2">
                            {s.features.map((f, i) => (
                                <Reveal key={f.title} delay={i * 0.06}>
                                    <div
                                        data-testid={`sector-feature-${i + 1}`}
                                        className="group flex h-full items-start gap-5 rounded-2xl border border-[#DCE5D4] bg-white p-7 transition-[border-color] duration-400 hover:border-[#598B3B]/50"
                                    >
                                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#598B3B]/10 text-[#598B3B]">
                                            <f.icon size={20} strokeWidth={1.6} />
                                        </span>
                                        <div>
                                            <h3 className="font-display text-xl font-medium tracking-tight text-[#0E1112]">
                                                {f.title}
                                            </h3>
                                            <p className="mt-2 text-sm leading-relaxed text-[#5C6B57]">
                                                {f.desc}
                                            </p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                )}

                {s.products && (
                    <div className="mt-16 rounded-3xl border border-[#DCE5D4] bg-[#F7F9F5] p-7 md:p-12">
                        <SectionNumber n="D" label={s.productsTitle} />
                        <p className="mb-10 max-w-xl text-base leading-relaxed text-[#45543F]">
                            {s.productsIntro}
                        </p>
                        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                            {s.products.map((p, i) => (
                                <Reveal key={p.name} delay={i * 0.06} className="h-full">
                                    <article
                                        data-testid={`sector-product-${i + 1}`}
                                        className="flex h-full flex-col overflow-hidden rounded-2xl border border-[#DCE5D4] bg-white transition-[border-color,transform] duration-400 hover:-translate-y-1.5 hover:border-[#598B3B]/50"
                                    >
                                        {p.img && (
                                            <img
                                                src={p.img}
                                                alt={p.name}
                                                loading="lazy"
                                                className="h-36 w-full object-cover"
                                            />
                                        )}
                                    <div className="flex flex-1 flex-col p-6">
                                        <div className="flex items-center justify-between">
                                            <span className="rounded-full bg-[#7CBF52]/15 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#4a7530]">
                                                {p.spec}
                                            </span>
                                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#598B3B]/10 text-[#598B3B]">
                                                <p.icon size={17} strokeWidth={1.6} />
                                            </span>
                                        </div>
                                        <h4 className="font-display mt-5 text-xl font-medium leading-snug tracking-tight text-[#0E1112]">
                                            {p.name}
                                        </h4>
                                        <p className="mt-2 text-sm leading-relaxed text-[#5C6B57]">
                                            {p.use}
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-1.5">
                                            {p.apps.map((a) => (
                                                <span
                                                    key={a}
                                                    className="rounded-md bg-[#F0F5EA] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#45543F]"
                                                >
                                                    {a}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="mt-auto flex items-center gap-2 pt-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#598B3B]">
                                            <span className="h-1.5 w-1.5 rounded-full bg-[#7CBF52]" />
                                            {p.industry}
                                        </p>
                                    </div>
                                    </article>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                )}

                <Reveal>
                    <div className="mt-14 flex flex-wrap gap-3" data-testid="sector-stats">
                        {s.stats.map((st) => (
                            <span
                                key={st}
                                className="rounded-full border border-[#598B3B]/35 bg-[#598B3B]/5 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-[#598B3B]"
                            >
                                {st}
                            </span>
                        ))}
                    </div>
                </Reveal>

                <Reveal>
                    <div className="mt-16 flex flex-col items-start justify-between gap-8 rounded-3xl bg-[#0E1112] p-8 md:flex-row md:items-center md:p-12">
                        <div>
                            <h3 className="font-display text-3xl font-light tracking-tight text-white md:text-4xl">
                                Talk to an expert about this solution
                            </h3>
                            <p className="mt-3 text-sm text-[#93A68E]">
                                NOMAS — Integrated Services and Solutions
                                Provider. We respond within one business day.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-4">
                            <a
                                href="mailto:gm@nomas-co.com?subject=Solution%20Enquiry"
                                data-testid="sector-email-btn"
                                className="flex items-center gap-2.5 rounded-full bg-[#7CBF52] px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#0E1112] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#8fd064]"
                            >
                                Get a Quote
                                <ArrowUpRight size={15} />
                            </a>
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="sector-whatsapp-btn"
                                className="flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#7CBF52] hover:bg-white/5"
                            >
                                WhatsApp Us
                            </a>
                        </div>
                    </div>
                </Reveal>
            </div>
        </main>
    );
}
